import * as ko from 'knockout'
import type { BetType } from '@mobi/api-types/racePage/types'
import { CHANGE_BET_TYPE_EVENT } from '@core/Areas/RaceCard/constants'
import { Disposable } from '../../../../AppUtils/Framework/Disposable/Disposable'
import Guard from '../../../../AppUtils/Framework/Guard'
import type { BettingInformation } from '../../../Model/BettingInformation'
import BettingInformationManager from '../../../Model/BettingInformationManager'
import type ObservableRacePage from '../../../Model/Observables/ObservableRacePage'
import type { SortOption } from '../../../Sorting/types'

const lastSelectedBetType: ko.Observable<BetType | null> = ko.observable(null)

export const resetMatchedRaceBetTypePersist = () => lastSelectedBetType(null)

export class BettingStateManagerViewModel extends Disposable {
  private bettingInformationManager: BettingInformationManager
  private meetingInformationSubscriptions!: ko.Subscription

  public bettingInformation!: BettingInformation
  public raceInformation!: ObservableRacePage
  public ready: ko.Observable<boolean> = ko.observable<boolean>(false).extend({ notify: 'always' })

  constructor(params: { raceInformation: ObservableRacePage }) {
    super()

    this.ready(false)
    Guard.notNull(params)
    Guard.notNull(params.raceInformation)
    Guard.notNull(params.raceInformation.meetingInformation)

    if (lastSelectedBetType() !== null) {
      const isLastBetTypeAvailable = params.raceInformation
        .availableBetTypes()
        .some(({ betType }) => betType() === lastSelectedBetType())
      if (!isLastBetTypeAvailable) resetMatchedRaceBetTypePersist()
    }

    this.raceInformation = params.raceInformation
    this.bettingInformation =
      params.raceInformation.provisionBettingInformation(lastSelectedBetType)
    this.bettingInformation.selections.setUpForLegs(params.raceInformation, false)
    this.bettingInformation.setUpFields()

    this.bettingInformationManager = new BettingInformationManager(
      this.raceInformation,
      this.bettingInformation
    )

    this.configureDisposal()
    this.setupSubscriptionsAndEventNotifications()
    this.ready(true)
  }

  protected setupSubscriptionsAndEventNotifications() {
    this.subscribeToMeetingChanges()
    this.subscribeToBetTypeChanges()
    this.subscribeToSortChange()
  }

  private subscribeToMeetingChanges() {
    this.meetingInformationSubscriptions = this.raceInformation.mergeDone.subscribe(mergeIsDone => {
      if (!mergeIsDone) {
        this.ready(false)
      } else {
        const oldRaceNumber = this.bettingInformation.raceNumber

        this.bettingInformation.merge(
          this.raceInformation.meetingInformation.meetingId(),
          this.raceInformation.meetingInformation.selectedRace.raceNumber(),
          this.raceInformation.meetingInformation.meetingDate(),
          this.raceInformation.betIdentifier,
          this.raceInformation.meetingInformation.selectedRace.fixedPlacesPaying()
        )

        const raceChanged = oldRaceNumber != this.bettingInformation.raceNumber
        this.bettingInformationManager.update(raceChanged)

        this.ready(true)
      }
    })
  }

  protected subscribeToBetTypeChanges() {
    this.safeSubscribe(CHANGE_BET_TYPE_EVENT, (params: { betType: BetType }) => {
      this.ready(false)
      this.raceInformation.resetStartersSelections()
      if (params.betType !== 'Mystery') lastSelectedBetType(params.betType)
      this.bettingInformationManager.processBetTypeChange(params.betType)
      this.ready(true)
    })
  }

  protected subscribeToSortChange() {
    this.safeSubscribe('sort-starters', (params: { raceNumber: number; option: SortOption }) => {
      this.ready(false)
      this.bettingInformationManager.processSort(params)
      this.bettingInformation.setSortOption(params.option)
      this.ready(true)
    })
  }

  private configureDisposal() {
    this.registerDisposals(() => this.meetingInformationSubscriptions.dispose())
  }
}
